import React, { Dispatch, useEffect, useState } from 'react';
import { withStyles, createStyles } from '@material-ui/core/styles';
import { CSButton, CSSwitch, Typography } from '../../../primitives';
import {
  Box,
  Grid,
  Divider,
  Hidden,
  Theme,
  IconButton,
} from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Transition } from '../../../confirmationDialog/ConfirmationDialog';
import TextField from '@material-ui/core/TextField';
import enumToLabel from '../../../../utils/enumToLabel';
import detailsPageStyles from '../../../commonStyles/detailsPage.style';
import classNames from 'classnames';
import { INTERACTION } from '../../utils';
import EditIcon from '../../../../utils/svgs/EditIcon';
import configPageStyles from '../../../commonStyles/configPage.style';
import Highlighter from 'react-highlight-words';
import { processSearchQuery } from '../searchQuery';
import { CurrentView, ModuleName } from '../Modules';
// Redux
import { useSearchResultSections } from '../../useSearchResultSections';

interface Props {
  classes: { [key: string]: string };
  data: any;
  interaction?: INTERACTION;
  setPartialData: Dispatch<any>;
  currentView: CurrentView;
}

const initDialogData = {
  title: '',
  key: null,
};

const DashboardModule: React.FC<Props> = ({
  classes,
  data,
  interaction,
  setPartialData,
  currentView,
}) => {
  const [modulesData, setModulesData] = useState<any[]>([]);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [dialogData, setDialogData] = useState<any>(initDialogData);

  const [searchResult, setSearchResult] = useState<string[]>([]);
  const [showWholeSection, setShowWholeSection] =
    useState<boolean>(true);

  const SECTION_TITLE = 'Dashboard Elements';

  const { searchQuery, updateSections } = useSearchResultSections(
    ModuleName.WEB,
    SECTION_TITLE,
  );

  useEffect(
    function setModuleData() {
      if (data && currentView !== CurrentView.SEARCH) {
        const tmpData: any[] = [];
        for (let item of Object.entries(data)) {
          const itemData = item[1] as {
            title: string;
          };

          tmpData.push(itemData);
        }
        setModulesData(tmpData);
      }
    },
    [data, currentView],
  );

  useEffect(
    function filterModuleDataBySearchResults() {
      if (currentView === CurrentView.SEARCH && data) {
        const tmpData: any[] = [];
        for (let item of Object.entries(data)) {
          const itemData = item[1] as {
            title: string;
          };
          if (
            showWholeSection ||
            searchResult.some(
              (result) =>
                itemData.title
                  .toLowerCase()
                  .includes(result.toLowerCase()) ||
                enumToLabelFunc(item[0])
                  .toLowerCase()
                  .includes(result.toLowerCase()),
            )
          )
            tmpData.push(itemData);
        }
        setModulesData(tmpData);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, searchResult],
  );

  const getElKey = (obj: any) => {
    const el = Object.entries(data).find(
      ([key, value]: any) => value === obj,
    );
    return el?.length ? el[0] : '';
  };

  const enumToLabelFunc = (txt?: string) => {
    if (!txt) {
      return '';
    } else if (txt === 'MANIFESTEDINBOUND') {
      return 'Manifested Inbound';
    } else if (txt === 'MANIFESTEDOUTBOUND') {
      return 'Manifested Outbound';
    }

    return enumToLabel(txt);
  };

  const onAfterDialogClose = () => {
    setShowDialog(false);
    setDialogData(initDialogData);
  };

  useEffect(
    function processSearch() {
      if (currentView === CurrentView.SEARCH) {
        //Show whole section is there are results in a title
        setShowWholeSection(
          processSearchQuery(searchQuery, [SECTION_TITLE]).length !==
            0,
        );

        const dynamicLabels: string[] = [];
        for (let item of Object.entries(data)) {
          const itemData = item[1] as {
            title: string;
          };
          dynamicLabels.push(itemData.title);
          dynamicLabels.push(enumToLabelFunc(item[0]));
        }

        const results = processSearchQuery(searchQuery, [
          SECTION_TITLE,
          ...dynamicLabels,
        ]);

        setSearchResult(results);
        updateSections(results);
      } else {
        setSearchResult([]);
      }
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchQuery, currentView],
  );

  const renderDialog = () => {
    return (
      <Dialog
        classes={{ paperScrollPaper: classes.dialogRoot }}
        open={showDialog}
        TransitionComponent={Transition}
        onClose={() => {
          onAfterDialogClose();
        }}
      >
        <DialogTitle>
          <Typography>Edit</Typography>
        </DialogTitle>
        <DialogContent className={classes.dialogRoot}>
          <TextField
            data-testid='dashboard-dialog-name'
            autoFocus
            InputLabelProps={{
              shrink: true,
              className: classes.inputNameLabel,
            }}
            InputProps={{
              disableUnderline: true,
              className: classes.inputName,
            }}
            classes={{
              root: classes.inputNameHolder,
            }}
            value={dialogData.title}
            onChange={(e) => {
              setDialogData({
                ...dialogData,
                title: e.target.value,
              });
            }}
          />
        </DialogContent>
        <DialogActions>
          <CSButton
            color='secondary'
            variant='outlined'
            onClick={() => {
              onAfterDialogClose();
            }}
          >
            Cancel
          </CSButton>
          <CSButton
            color='secondary'
            variant='contained'
            data-testid='dashboard-dialog-save-btn'
            onClick={() => {
              data[dialogData.key].title = dialogData.title;
              setPartialData({
                ...data,
              });
              onAfterDialogClose();
            }}
          >
            Update
          </CSButton>
        </DialogActions>
      </Dialog>
    );
  };
  const renderComponent = () => {
    return (
      <>
        {renderDialog()}

        <Grid item sm={12} style={{ width: '100%' }}>
          <Box>
            <Box
              className={classNames(
                classes.moduleTitle,
                classes.pLeft,
              )}
              mb={1}
              mt={2}
            >
              <Highlighter
                highlightClassName={classes.searchResultsMark}
                searchWords={searchResult}
                autoEscape={true}
                textToHighlight={SECTION_TITLE}
              />
            </Box>

            <Box mt={2}>
              <Box mb={2}>
                <Grid
                  container
                  item
                  sm={12}
                  className={classes.textHeader}
                >
                  <Grid item sm={2} xs={7} className={classes.pLeft}>
                    Item
                  </Grid>
                  <Hidden xsDown>
                    <Grid item sm={8}>
                      Title
                    </Grid>
                  </Hidden>
                  <Grid item sm={1} xs={2} />
                  <Grid item sm={1} xs={3}>
                    Active
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Divider className={classes.divider} />
                </Grid>
              </Box>
              {modulesData?.map(
                (partialDataEl: any, index: number, array: any[]) => {
                  return (
                    <Grid
                      container
                      item
                      sm={12}
                      key={partialDataEl.title}
                      className={classes.row}
                    >
                      <Grid
                        item
                        sm={2}
                        xs={7}
                        className={classNames(
                          classes.text,
                          classes.pLeft,
                        )}
                      >
                        <Highlighter
                          highlightClassName={
                            classes.searchResultsMark
                          }
                          searchWords={searchResult}
                          autoEscape={true}
                          textToHighlight={enumToLabelFunc(
                            getElKey(partialDataEl),
                          )}
                        />
                      </Grid>
                      <Hidden xsDown>
                        <Grid item sm={8} className={classes.text}>
                          <Highlighter
                            highlightClassName={
                              classes.searchResultsMark
                            }
                            searchWords={searchResult}
                            autoEscape={true}
                            textToHighlight={partialDataEl.title}
                          />
                        </Grid>
                      </Hidden>
                      <Grid item sm={1} xs={2}>
                        <IconButton
                          data-testid={`dashboard-${partialDataEl.title}-edit-btn`}
                          size='small'
                          disabled={interaction === INTERACTION.READ}
                          className={classes.editIcon}
                          onClick={() => {
                            setDialogData({
                              title: partialDataEl.title,
                              key: getElKey(partialDataEl),
                            });
                            setShowDialog(true);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Grid>
                      <Grid
                        item
                        sm={1}
                        xs={3}
                        className={classes.muiSwitchHoler}
                      >
                        <CSSwitch
                          data-testid={`dashboard-${partialDataEl.title}-switch-active`}
                          color='default'
                          size='small'
                          disabled={interaction === INTERACTION.READ}
                          checked={partialDataEl.active}
                          onChange={() => {
                            data[getElKey(partialDataEl)].active =
                              !partialDataEl.active;
                            setPartialData({
                              ...data,
                            });
                          }}
                        />
                      </Grid>
                      {index !== array.length - 1 && (
                        <Grid item xs={12}>
                          <Divider className={classes.divider} />
                        </Grid>
                      )}
                    </Grid>
                  );
                },
              )}
            </Box>
          </Box>
        </Grid>
      </>
    );
  };

  return currentView === CurrentView.SEARCH && !searchResult.length
    ? null
    : renderComponent();
};

export default withStyles(
  createStyles((theme: Theme) => ({
    ...detailsPageStyles,
    ...configPageStyles(theme),
  })),
)(DashboardModule);
